import { useState } from 'react';

import {
  money,
  rangeContainsMs,
  toPayoutStatementCsv,
  type AgentPayout,
  type LedgerRange,
  type PayoutCsvLabels,
} from '@kilat/shared';

import { useI18n } from '../../lib/use-i18n.js';
import { downloadCsv } from '../../lib/csv-download.js';
import { FilterPills, type FilterOption } from '../../components/filter-pills.js';
import { ExportCsvButton } from '../../components/export-csv-button.js';
import { PayoutStatusPill } from '../../components/payout-status-pill.js';

/**
 * Presentational payout-history surface shared by `/payouts` (agent) and
 * `/recruiter/payouts` (recruiter). The route owns auth + the live
 * subscription + the top-level no-dead-ends states (signed-out / not-agent /
 * loading / error / empty); this module owns the populated render: hero,
 * range + status filters, per-batch cards, and the CSV export.
 *
 * MONEY ACCURACY: `grossAmount` is integer sen; every sum here adds integers
 * and `money.format` performs the single divide-by-100 at the display edge.
 */

type PayoutStatusFilter = 'all' | AgentPayout['status'];

export function PayoutsPageShell({ children }: { readonly children: React.ReactNode }) {
  return (
    <main
      className="ag-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--tint-subtle)',
        padding: 'var(--space-4) var(--space-4) var(--space-12) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      {children}
    </main>
  );
}

export function PayoutsHero({ payouts }: { readonly payouts: readonly AgentPayout[] }) {
  const t = useI18n();
  let paidSen = 0;
  let inProgressSen = 0;
  for (const p of payouts) {
    if (p.status === 'paid') paidSen += p.grossAmount;
    else if (p.status === 'pending' || p.status === 'processing') inProgressSen += p.grossAmount;
  }
  return (
    <section
      style={{
        background: 'var(--color-primary)',
        color: 'var(--color-primary-fg)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-2)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          opacity: 0.8,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {t.t('agent.payouts.paidToDate')}
      </p>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-display)',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {money.format(paidSen)}
      </p>
      <div
        style={{
          marginTop: 'var(--space-4)',
          display: 'flex',
          gap: 'var(--space-6)',
          flexWrap: 'wrap',
          fontSize: 'var(--text-meta)',
          opacity: 0.92,
        }}
      >
        <span>
          {t.t('agent.payouts.inProgress')}{' '}
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
            {money.format(inProgressSen)}
          </strong>
        </span>
        <span>
          {t.t('agent.payouts.batches')} <strong>{payouts.length}</strong>
        </span>
      </div>
    </section>
  );
}

export function PayoutsList({
  payouts,
  csvFilenamePrefix,
}: {
  readonly payouts: readonly AgentPayout[];
  readonly csvFilenamePrefix: string;
}) {
  const t = useI18n();
  const [range, setRange] = useState<LedgerRange>('all_time');
  const [status, setStatus] = useState<PayoutStatusFilter>('all');
  const nowMs = Date.now();

  const rangeOptions: FilterOption<LedgerRange>[] = [
    { value: 'this_month', label: t.t('agent.range.thisMonth') },
    { value: 'last_month', label: t.t('agent.range.lastMonth') },
    { value: 'all_time', label: t.t('agent.range.allTime') },
  ];
  const statusOptions: FilterOption<PayoutStatusFilter>[] = [
    { value: 'all', label: t.t('agent.payouts.statusFilterAll') },
    { value: 'pending', label: t.t('agent.payouts.status.pending') },
    { value: 'processing', label: t.t('agent.payouts.status.processing') },
    { value: 'paid', label: t.t('agent.payouts.status.paid') },
    { value: 'failed', label: t.t('agent.payouts.status.failed') },
  ];

  const filtered = payouts
    .filter((p) => rangeContainsMs(tsMs(p.initiatedAt), range, nowMs))
    .filter((p) => status === 'all' || p.status === status)
    .slice()
    .sort((a, b) => tsMs(b.initiatedAt) - tsMs(a.initiatedAt));

  const onExport = () => {
    const labels: PayoutCsvLabels = {
      payoutId: t.t('agent.payouts.csv.payoutId'),
      periodStart: t.t('agent.payouts.csv.periodStart'),
      periodEnd: t.t('agent.payouts.csv.periodEnd'),
      status: t.t('agent.payouts.csv.status'),
      method: t.t('agent.payouts.csv.method'),
      gross: t.t('agent.payouts.csv.gross'),
      items: t.t('agent.payouts.csv.items'),
      initiated: t.t('agent.payouts.csv.initiated'),
      paid: t.t('agent.payouts.csv.paid'),
      txRef: t.t('agent.payouts.csv.txRef'),
    };
    downloadCsv(
      `${csvFilenamePrefix}-${dateStamp(nowMs)}.csv`,
      toPayoutStatementCsv(filtered, labels),
    );
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
        }}
      >
        <FilterPills
          ariaLabel={t.t('agent.range.ariaLabel')}
          value={range}
          options={rangeOptions}
          onChange={setRange}
        />
        <ExportCsvButton
          label={t.t('agent.payouts.exportCsv')}
          onClick={onExport}
          disabled={filtered.length === 0}
        />
      </div>
      <FilterPills
        ariaLabel={t.t('agent.payouts.statusFilterAria')}
        value={status}
        options={statusOptions}
        onChange={setStatus}
      />
      {filtered.length === 0 ? (
        <PayoutsStateCard
          title={t.t('agent.payouts.filteredEmptyTitle')}
          body={t.t('agent.payouts.filteredEmptyBody')}
        />
      ) : (
        <ul
          className="ag-grid"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
          }}
        >
          {filtered.map((p) => (
            <PayoutCard key={p.id} payout={p} />
          ))}
        </ul>
      )}
    </>
  );
}

function PayoutCard({ payout }: { readonly payout: AgentPayout }) {
  const t = useI18n();
  const methodLabel =
    payout.method === 'duitnow'
      ? t.t('agent.payouts.methodDuitnow')
      : t.t('agent.payouts.methodBankTransfer');
  return (
    <li
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        boxShadow: 'var(--shadow-1)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 'var(--space-2)',
        }}
      >
        <PayoutStatusPill status={payout.status} />
        <strong style={{ fontVariantNumeric: 'tabular-nums', fontSize: 'var(--text-h2)' }}>
          {money.format(payout.grossAmount)}
        </strong>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t.t('agent.payouts.period')} {formatDate(tsMs(payout.periodStart))} –{' '}
        {formatDate(tsMs(payout.periodEnd))}
      </p>
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
        }}
      >
        <span>
          {t.t('agent.payouts.method')} {methodLabel}
        </span>
        <span>{t.t('agent.payouts.items', { count: payout.items.length })}</span>
        {payout.paidAt ? (
          <span>
            {t.t('agent.payouts.paidOn')} {formatDate(tsMs(payout.paidAt))}
          </span>
        ) : null}
        {payout.txRef ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {t.t('agent.payouts.txRef')} {payout.txRef}
          </span>
        ) : null}
      </div>
    </li>
  );
}

export function PayoutsStateCard({
  title,
  body,
  ctaLabel,
  ctaHref,
  ctaNote,
  onRetry,
  retryLabel,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaHref?: string;
  readonly ctaNote?: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        textAlign: 'center',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 420,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {onRetry && retryLabel ? (
        <button
          type="button"
          onClick={onRetry}
          style={{
            display: 'inline-block',
            marginTop: 'var(--space-4)',
            minHeight: 'var(--touch-target, 44px)',
            padding: 'var(--space-3) var(--space-6)',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            border: 'none',
            fontWeight: 500,
            fontSize: 'var(--text-meta)',
            cursor: 'pointer',
          }}
        >
          {retryLabel}
        </button>
      ) : null}
      {ctaLabel && ctaHref ? (
        <a
          href={ctaHref}
          style={{
            display: 'inline-block',
            marginTop: 'var(--space-4)',
            padding: 'var(--space-3) var(--space-6)',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: 'var(--text-meta)',
          }}
        >
          {ctaLabel}
        </a>
      ) : null}
      {ctaNote ? (
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {ctaNote}
        </p>
      ) : null}
    </section>
  );
}

export function PayoutsHeroSkeleton() {
  const t = useI18n();
  return (
    <section
      style={{
        background: 'var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        height: 168,
      }}
      aria-busy="true"
      aria-label={t.t('agent.payouts.loadingAria')}
    />
  );
}

export function PayoutsListSkeleton() {
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      aria-busy="true"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 96,
            borderRadius: 'var(--radius-lg)',
            background: 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

function tsMs(ts: { toMillis?: () => number; seconds?: number } | unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function dateStamp(ms: number): string {
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}
